import { useEffect, useState } from 'react'
import { BasicModal } from '../GenericModals/BasicModal'

export const CustomGeneralBiomarkerModal = ({
    showModal,
    setShowModal,
    selectedFeedings,
    biomarkerOptions,
    setBiomarkerOptions,
    setLoading
}) => {

    const [showVerificationText, setShowVerificationText] = useState(false);
    const [actionButtonTitle, setActionButtonTitle] = useState("Generate");

    // Validation errors state
    const [errors, setErrors] = useState({});

    useEffect(() => {
        setShowVerificationText(false);
        setErrors({}); // Clear errors when options change
    }, [biomarkerOptions]);

    useEffect(() => {
      setActionButtonTitle(showVerificationText ? "Yes, generate" : "Generate");
    }, [showVerificationText])

    // Validation helper functions
    const isValidInteger = (value) => {
        const num = Number(value);
        return !isNaN(num) && Number.isInteger(num);
    };

    const closeModal = () => {
        setShowModal();
        setShowVerificationText(false);
        setErrors({});
    }

    const validateSucks = (value) => {
        if (!isValidInteger(value)) {
            return 'Must be a whole number';
        }
        const num = Number(value);
        if (num < 1) {
            return 'Minimum is 1 suck';
        }
        return null;
    };

    const validateMinutes = (value) => {
        if (!isValidInteger(value)) {
            return 'Must be a whole number';
        }
        const num = Number(value);
        if (num < 0) {
            return 'Minimum is 0';
        }
        return null;
    };

    const validateSeconds = (value) => {
        if (!isValidInteger(value)) {
            return 'Must be a whole number';
        }
        const num = Number(value);
        if (num < 0 || num > 59) {
            return 'Must be 0-59';
        }
        return null;
    };

    const validateForm = () => {
        const newErrors = {};

        // Validate start point inputs
        if (biomarkerOptions.startPoint === 'sucks-within') {
            const sucksError = validateSucks(biomarkerOptions.startSucks);
            if (sucksError) {
                newErrors.startSucks = sucksError;
            }
        }

        if (biomarkerOptions.startPoint === 'specific-time') {
            const startMmError = validateMinutes(biomarkerOptions.startTime.mm);
            const startSsError = validateSeconds(biomarkerOptions.startTime.ss);
            if (startMmError) {
                newErrors.startTimeMm = startMmError;
            }
            if (startSsError) {
                newErrors.startTimeSs = startSsError;
            }
        }

        // Validate end point inputs
        if (biomarkerOptions.endPoint === 'duration' || biomarkerOptions.endPoint === 'specific-time') {
            const endMmError = validateMinutes(biomarkerOptions.endTime.mm);
            const endSsError = validateSeconds(biomarkerOptions.endTime.ss);
            if (endMmError) {
                newErrors.endTimeMm = endMmError;
            }
            if (endSsError) {
                newErrors.endTimeSs = endSsError;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onStartPointChange = ({ target }) => {
        const { value } = target;
        ////console.log("Selected start point:", value);
        setBiomarkerOptions((prevOptions) => ({
            ...prevOptions,
            startPoint: value
        }));
    }

    const onEndPointChange = ({ target }) => {
        const { value } = target;
        //console.log("Selected end point:", value);
        setBiomarkerOptions((prevOptions) => ({
            ...prevOptions,
            endPoint: value
        }));
    }

    const onStartSucksChange = ({ target }) => {
        const { value } = target;
        setBiomarkerOptions((prevOptions) => ({
            ...prevOptions,
            startSucks: value
        }));
    }

    const onStartTimeChange = ({ target }) => {
        const { name, value } = target;
        setBiomarkerOptions((prevOptions) => ({
            ...prevOptions,
            startTime: {
                ...prevOptions.startTime,
                [name]: value
            },
        }));
    }

    const onEndTimeChange = ({ target }) => {
        const { name, value } = target;
        setBiomarkerOptions((prevOptions) => ({
            ...prevOptions,
            endTime: {
                ...prevOptions.endTime,
                [name]: value
            },
        }));
    }

    const onSubmit = () => {
        //console.log("Generating biomarkers with options:", biomarkerOptions);

        // Validate before showing verification
        if (!validateForm()) {
            return;
        }

        if (!showVerificationText) {
            setShowVerificationText(true);
            return;
        }

        // Proceed with biomarker generation
        closeModal();
        setLoading(true);
    }

    const getStartPointDescription = () => {
        switch (biomarkerOptions.startPoint) {
            case 'sucks-within':
                return `First burst of at least ${biomarkerOptions.startSucks} consecutive sucks with no more than 2 seconds between each`;
            case 'first-suck':
                return 'First suck in the feeding session';
            case 'specific-time':
                return `${biomarkerOptions.startTime.mm} minutes, ${biomarkerOptions.startTime.ss} seconds`;
            case 'first-breath':
                return 'First breath';
            case 'first-swallow':
                return 'First swallow';
            default:
                return '';
        }
    }

    const getEndPointDescription = () => {
        switch (biomarkerOptions.endPoint) {
            case 'duration':
                if (biomarkerOptions.startPoint === 'specific-time') {
                    let seconds = Number(biomarkerOptions.endTime.ss) + Number(biomarkerOptions.startTime.ss);
                    let extraMinutes = 0;
                    if (seconds >= 60) {
                        extraMinutes = Math.floor(seconds / 60);
                        seconds = seconds % 60;
                    }
                    const minutes = Number(biomarkerOptions.endTime.mm) + Number(biomarkerOptions.startTime.mm) + extraMinutes;
                    return `${minutes} minutes, ${seconds} seconds`;
                } else {
                    const start = biomarkerOptions.startPoint === 'sucks-within' ? 'first burst' : biomarkerOptions.startPoint.replace(/-/g, ' ');
                    return `${biomarkerOptions.endTime.mm} minutes, ${biomarkerOptions.endTime.ss} seconds after the ${start}`;
                }
            case 'specific-time':
                return `${biomarkerOptions.endTime.mm} minutes, ${biomarkerOptions.endTime.ss} seconds`;
            case 'last-suck':
                return 'Last suck + 10 seconds';
            default:
                return '';
        }
    }

    // Helper to get input class with error styling
    const getInputClass = (hasError) => {
        return `form-control ${hasError ? 'is-invalid' : ''}`;
    };

    return (
        <BasicModal
            showModal={showModal}
            title="Custom Biomarkers"
            successFunction={onSubmit}
            onHide={closeModal}
            actionButtonTitle= {actionButtonTitle}
        >
            <div className="d-flex flex-column w-100">
                {/* Start Point */}
                <div className="w-100 mb-4">
                    <h6>Start Point</h6>

                    <div className="form-check d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex">
                            <input
                                className={`neo_input_radio form-check-input me-2`}
                                type="radio"
                                name="startPoint"
                                id="firstSucksWithin"
                                value="sucks-within"
                                checked={biomarkerOptions.startPoint === 'sucks-within'}
                                onChange={onStartPointChange}
                            />
                            <label className="form-check-label" htmlFor="firstSucksWithin">
                                First burst (min. consecutive sucks within 2 seconds)
                            </label>
                        </div>
                        {biomarkerOptions.startPoint === 'sucks-within' &&
                            <div className="flex ms-2">
                                <label htmlFor='sucks'>sucks</label>
                                <input
                                    id='sucks'
                                    className={getInputClass(errors.startSucks)}
                                    style={{ width: "90px" }}
                                    type="number"
                                    placeholder="sucks"
                                    value={biomarkerOptions.startSucks}
                                    onChange={onStartSucksChange}
                                />
                                {errors.startSucks && (
                                    <div className="neo_invalid_feedback d-block">
                                        {errors.startSucks}
                                    </div>
                                )}
                            </div>
                        }
                    </div>

                    <div className="form-check d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex">
                            <input
                                className="neo_input_radio form-check-input me-2"
                                type="radio"
                                name="startPoint"
                                id="firstSuck"
                                value="first-suck"
                                checked={biomarkerOptions.startPoint === 'first-suck'}
                                onChange={onStartPointChange}
                            />
                            <label className="form-check-label" htmlFor="firstSuck">
                                First suck in the feeding session
                            </label>
                        </div>
                    </div>

                    <div className="form-check d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex">
                            <input
                                className="neo_input_radio form-check-input me-2"
                                type="radio"
                                name="startPoint"
                                id="specificStartTime"
                                value="specific-time"
                                checked={biomarkerOptions.startPoint === 'specific-time'}
                                onChange={onStartPointChange}
                            />
                            <label className="form-check-label" htmlFor="specificStartTime">
                                Specific start time (mm:ss)
                            </label>
                        </div>
                        {biomarkerOptions.startPoint === 'specific-time' &&
                            <div className="d-flex">
                                <div className="flex">
                                    <label htmlFor='specificStartTimeMM'>min</label>
                                    <input
                                        id='specificStartTimeMM'
                                        className={`${getInputClass(errors.startTimeMm)} me-1`}
                                        style={{ width: "80px" }}
                                        type="number"
                                        placeholder="mm"
                                        name='mm'
                                        value={biomarkerOptions.startTime.mm}
                                        onChange={onStartTimeChange}
                                    />
                                    {errors.startTimeMm && (
                                        <div className="neo_invalid_feedback d-block" >
                                            {errors.startTimeMm}
                                        </div>
                                    )}
                                </div>
                                <div className="flex">
                                    <label htmlFor='specificStartTimeSS'>sec</label>
                                    <input
                                        id='specificStartTimeSS'
                                        className={getInputClass(errors.startTimeSs)}
                                        style={{ width: "80px" }}
                                        type="number"
                                        placeholder="ss"
                                        name="ss"
                                        value={biomarkerOptions.startTime.ss}
                                        onChange={onStartTimeChange}
                                    />
                                    {errors.startTimeSs && (
                                        <div className="neo_invalid_feedback d-block" >
                                            {errors.startTimeSs}
                                        </div>
                                    )}
                                </div>
                            </div>
                        }
                    </div>

                    <div className="form-check d-flex align-items-center mb-2">
                        <div className="d-flex">
                            <input
                                className="neo_input_radio form-check-input me-2"
                                type="radio"
                                name="startPoint"
                                id="firstBreath"
                                value="first-breath"
                                checked={biomarkerOptions.startPoint === 'first-breath'}
                                onChange={onStartPointChange}
                                disabled="true"
                            />
                            <label className="form-check-label" htmlFor="firstBreath">
                                First breath
                            </label>
                        </div>
                    </div>

                    <div className="form-check d-flex align-items-center mb-2">
                        <div className="d-flex">
                            <input
                                className="neo_input_radio form-check-input me-2"
                                type="radio"
                                name="startPoint"
                                id="firstSwallow"
                                value="first-swallow"
                                checked={biomarkerOptions.startPoint === 'first-swallow'}
                                onChange={onStartPointChange}
                                disabled="true"
                            />
                            <label className="form-check-label" htmlFor="firstSwallow">
                                First swallow
                            </label>
                        </div>
                    </div>
                </div>

                {/* End Point */}
                <div className="w-100 mb-4">
                    <h6>End Point</h6>

                    <div className="form-check d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex">
                            <input
                                className="neo_input_radio form-check-input me-2"
                                type="radio"
                                name="endPoint"
                                id="duration"
                                value="duration"
                                checked={biomarkerOptions.endPoint === 'duration'}
                                onChange={onEndPointChange}
                            />
                            <label className="form-check-label" htmlFor="duration">
                                Analyisis duration (mm:ss)
                            </label>
                        </div>

                        {biomarkerOptions.endPoint === 'duration' &&
                            <div className="d-flex">
                                <div className="flex">
                                    <label htmlFor='durationMM'>min</label>
                                    <input
                                        id='durationMM'
                                        className={`${getInputClass(errors.endTimeMm)} me-1`}
                                        style={{ width: "80px" }}
                                        type="number"
                                        placeholder="mm"
                                        name='mm'
                                        value={biomarkerOptions.endTime.mm}
                                        onChange={onEndTimeChange}
                                    />
                                    {errors.endTimeMm && (
                                        <div className="neo_invalid_feedback d-block" >
                                            {errors.endTimeMm}
                                        </div>
                                    )}
                                </div>
                                <div className="flex">
                                    <label htmlFor='durationSS'>sec</label>
                                    <input
                                        id='durationSS'
                                        className={getInputClass(errors.endTimeSs)}
                                        style={{ width: "80px" }}
                                        type="number"
                                        placeholder="ss"
                                        name='ss'
                                        value={biomarkerOptions.endTime.ss}
                                        onChange={onEndTimeChange}
                                    />
                                    {errors.endTimeSs && (
                                        <div className="neo_invalid_feedback d-block">
                                            {errors.endTimeSs}
                                        </div>
                                    )}
                                </div>
                            </div>
                        }
                    </div>

                    <div className="form-check d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex">
                            <input
                                className="neo_input_radio form-check-input me-2"
                                type="radio"
                                name="endPoint"
                                id="specificEndTime"
                                value="specific-time"
                                checked={biomarkerOptions.endPoint === 'specific-time'}
                                onChange={onEndPointChange}
                            />
                            <label className="form-check-label" htmlFor="specificEndTime">
                                Specific end time (mm:ss)
                            </label>
                        </div>
                        {biomarkerOptions.endPoint === 'specific-time' &&
                            <div className="d-flex">
                                <div className="flex">
                                    <label htmlFor='specificEndTimeMM'>min</label>
                                    <input
                                        className={`${getInputClass(errors.endTimeMm)} me-1`}
                                        style={{ width: "80px" }}
                                        type="number"
                                        placeholder="mm"
                                        id='specificEndTimeMM'
                                        name='mm'
                                        value={biomarkerOptions.endTime.mm}
                                        onChange={onEndTimeChange}
                                    />
                                    {errors.endTimeMm && (
                                        <div className="neo_invalid_feedback d-block" >
                                            {errors.endTimeMm}
                                        </div>
                                    )}
                                </div>
                                <div className="flex">
                                    <label htmlFor='specificEndTimeSS'>sec</label>
                                    <input
                                        id='specificEndTimeSS'
                                        className={getInputClass(errors.endTimeSs)}
                                        style={{ width: "80px" }}
                                        type="number"
                                        placeholder="ss"
                                        name='ss'
                                        value={biomarkerOptions.endTime.ss}
                                        onChange={onEndTimeChange}
                                    />
                                    {errors.endTimeSs && (
                                        <div className="neo_invalid_feedback d-block" >
                                            {errors.endTimeSs}
                                        </div>
                                    )}
                                </div>
                            </div>
                        }
                    </div>

                    <div className="form-check d-flex align-items-center mb-2">
                        <div className="d-flex">
                            <input
                                className="neo_input_radio form-check-input me-2"
                                type="radio"
                                name="endPoint"
                                id="lastSuck"
                                value="last-suck"
                                checked={biomarkerOptions.endPoint === 'last-suck'}
                                onChange={onEndPointChange}
                            />
                            <label className="form-check-label" htmlFor="lastSuck">
                                Last suck + 10 seconds
                            </label>
                        </div>
                    </div>
                </div>

                {showVerificationText &&
                    <>
                        <hr />

                        <div className="w-100 d-flex justify-content-center">
                            <div>
                                <h6>Are you sure?</h6>
                                <p>Biomarkers will be generated for <strong>{ selectedFeedings.length } feeding{ selectedFeedings.length > 1 && 's' }</strong> with the following criteria:</p>
                                <p><strong>Start analysis at:</strong> { getStartPointDescription() }</p>
                                <p><strong>End analysis at:</strong> { getEndPointDescription() } </p>
                            </div>
                        </div>
                    </>

                }
            </div>
        </BasicModal>

    )
}
